import React, { useContext, useEffect, useState } from 'react'

import AuthContext from '../../context/AuthContext';
import ModalContext from '../../context/ModalContext';

import { toast } from 'react-toastify';

import ChooseCompany from '../../components/CreateCouponV2/ChooseCompany/ChooseCompany';
import HeaderStepper from '../../components/CreateCouponV2/HeaderStepper/HeaderStepper';
import CreateCouponSection from '../../components/CreateCouponV2/CreateCoupon/CreateCouponSection';
import TermsInstructions from '../../components/CreateCouponV2/TermsInstructions/TermsInstructions';
import CustomButton from '../../common/CustomButton';
import { CircularProgress } from '@mui/material';
import CouponDetails from '../../components/CreateCouponV2/CouponDetails/CouponDetails';
import Location from '../../components/CreateCouponV2/Location/Location';
import ConfirmCoupon from '../../components/CreateCouponV2/ConfirmCoupon/ConfirmCoupon';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCouponContextV2 } from '../../context/CouponContextV2';

export default function CreateCouponV2() {

  const modalCtx = useContext(ModalContext);
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();

  const { couponState: coupon, updateCouponState: updateCoupon, clearState: clearCoupon } = useCouponContextV2();
  const [errors, setErrors] = useState({
    couponDeal: {
      message: "Please enter the coupon deal*",
      show: false
    },
    couponDescription: {
      message: "Please add the coupon description*",
      show: false
    }, 
    terms: {
      message: "Please select the terms and conditions*",
      show: false
    },
    instructions: {
      message: "Please select the instructions*",
      show: false
    },
    website: {
      message: "Please enter coupon website URL*",
      show: false
    },
    countries: {
      message: "Please enter eligible countries*",
      show: false
    },
    phone: {
      message: "Please enter mobile number*",
      show: false
    },
    mail: {
      message: "Please enter email address*",
      show: false
    },
    keywords: {
      message: "Please add coupon keywords*",
      show: false
    },
    code: {
      message: "Please enter coupon code*",
      show: false
    },
    location: {
      message: "Please select a location*",
      show: false
    },
    cta: {
      message: "Please select the action buttons*",
      show: false
    },
    categories: {
      message: "Please select categories*",
      show: false
    },
    contactMethod: {
      message: "Please provide at least one (website, phone, or email)*",
      show: false
    }
  })

  const steps = [
    'Deal',
    'Terms & Conditions',
    'Coupon Details',
    'Location',
    'Confirm'
  ];

  // Common
  // Check if we're in new coupon mode from URL
  const urlParams = new URLSearchParams(location.search);
  const isNewCouponMode = urlParams.get('mode') === 'new';
  
  // Initialize state - if new coupon mode, always start at step 0 with no company
  // Otherwise, check if we have a selected company (edit mode)
  const [currentStep, setCurrentStep] = useState(() => {
    if (isNewCouponMode) return 0;
    return (coupon.selectedCompany && !coupon.newCoupon) ? 1 : 0;
  });
  const [couponId, setCouponId] = useState(modalCtx.pageUpdate?.couponId || null);
  const [selectedCompany, setSelectedCompany] = useState(() => {
    if (isNewCouponMode) return null;
    return (coupon.selectedCompany && !coupon.newCoupon) ? coupon.selectedCompany : null;
  });
  const [submittingCoupon, setSubmittingCoupon] = useState(false);

  // Step 0
  const couponDealOption = coupon?.couponDealOption;
  const setCouponDealOption = (_couponDealOption) => {
    updateCoupon({couponDealOption: _couponDealOption})
  }
  
  const validFor = coupon?.validFor;
  const setValidFor = (_validFor) => {
    updateCoupon({validFor: _validFor})
  }

  // Step2
  const termsConditions = coupon?.termsConditions;
  const setTermsConditions = (_terms) => {
    updateCoupon({termsConditions: _terms})
  }
  
  const instructions = coupon?.instructions;
  const setInstructions = (_instructions) => {
    updateCoupon({instructions: _instructions})
  }

  // Step 3
  const couponDetails = coupon?.couponDetails;
  const setCouponDetails = (_couponDetails) => {
    updateCoupon({couponDetails: _couponDetails})
  }
  const [orderedCategoriesData, setOrderedCategoriesData] = useState([]);

  // Step 4
  const locationDetails = coupon?.locationDetails;
  const setLocationDetails = (_locationDetails) => {
    if (typeof _locationDetails === 'function') {
      const newLocationDetails = _locationDetails(coupon?.locationDetails);
      updateCoupon({locationDetails: newLocationDetails});
    } else {
      updateCoupon({locationDetails: _locationDetails});
    }
  }

  useEffect(() => {
    if (modalCtx.pageUpdate?.page === "CreateCouponV2") {
      const couponId = modalCtx.pageUpdate?.couponId;
      let keywords = modalCtx.pageUpdate?.keywords || [];
      if (typeof keywords === "string") {
        keywords = keywords.split('|').map(kw => kw.trim()).filter(Boolean);
      }
      if (couponId) {
        setCouponId(couponId);
        setCouponDetails({...couponDetails, keywords: keywords});
      }
      modalCtx.setPageUpdate({ page: "" });
    }
  }, [modalCtx.pageUpdate]);

  // Clear coupon data when component mounts for new coupon creation
  useEffect(() => {
    // Check URL parameters to determine if this is a new coupon creation
    const urlParams = new URLSearchParams(location.search);
    const isNewCouponMode = urlParams.get('mode') === 'new';
    
    if (isNewCouponMode) {
      // Clear state immediately when mode=new is detected
      clearCoupon();
      setCurrentStep(0);
      setSelectedCompany(null);
      setCouponId(null);
    }
  }, [location.search]);

  useEffect(() => {
    // Check if we're in edit mode (has couponId or editCouponState was called)
    // Only run this if we're NOT in new coupon mode
    const urlParams = new URLSearchParams(location.search);
    const isNewCouponMode = urlParams.get('mode') === 'new';
    
    if (!isNewCouponMode && (couponId || (!coupon.newCoupon && coupon.couponId))) {
      // For edit mode, start from step 1 (Deal part) instead of step 0
      setCurrentStep(1);
      setSelectedCompany(coupon.selectedCompany);
      setCouponId(coupon.couponId || couponId);
    }
  }, [couponId, coupon.newCoupon, coupon.selectedCompany, coupon.couponId, location.search]);

  // Sync selectedCompany from coupon context - but only if not in new coupon mode
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const isNewCouponMode = urlParams.get('mode') === 'new';
    
    // Don't sync if we're creating a new coupon
    if (isNewCouponMode) {
      return;
    }
    
    // Only sync if we're in edit mode (not a new coupon) and selectedCompany exists
    if (!coupon.newCoupon && coupon.selectedCompany && coupon.selectedCompany !== selectedCompany) {
      setSelectedCompany(coupon.selectedCompany);
    }
  }, [coupon.selectedCompany, coupon.newCoupon, location.search, selectedCompany]);

  const updateDealDetails = async () => {
    if (!couponId) return false;
    
    try {
      // Handle extend expiry time logic
      let finalValidFor = null; // Don't send validFor when extending expiry
      let finalValidTill = null;
      
      if (couponDealOption.extendExpiryTime && !couponDealOption.couponExpiration) {
        // For extend expiry, send the additional days and calculated validTill date
        if (couponDealOption.validTill) {
          finalValidTill = couponDealOption.validTill;
        }
        
        // Handle date picker case - use the calculated validFor from date picker
        if (couponDealOption.selectedExpiryDate && couponDealOption.validFor) {
          finalValidFor = couponDealOption.validFor;
        } else if (couponDealOption.additionalDays) {
          // Handle additional days case
          finalValidFor = parseInt(validFor) * 1 + parseInt(couponDealOption.additionalDays);
        }
      } else if (couponDealOption.couponExpiration) {
        finalValidFor = 99999;
        finalValidTill = null;
      } else {
        // For normal cases, send validFor as days
        finalValidFor = validFor;
      }

      const payload = {
        couponCode: couponDetails.couponCode,
        deal: getSelectedDeal(),
        dealItem: getSelectedDealItem(),
        dealUnit: getSelectedDealUnit(),
        description: couponDealOption.description,
        ...(finalValidFor !== null && { validFor: finalValidFor }), // Only send validFor if not null
        ...(finalValidTill !== null && { validTill: finalValidTill }), // Only send validTill if not null
        ...(couponDealOption.extendExpiryTime && couponDealOption.additionalDays && { additionalDays: parseInt(couponDealOption.additionalDays) }), // Send additional days when extending expiry
        ...(couponDealOption.extendExpiryTime && coupon.originalValidFor && { initialValidFor: parseInt(coupon.originalValidFor) }), // Send original validFor in edit mode when extending expiry
        timeframe: finalValidFor === 99999 ? 'No expiry' : (couponDealOption.extendExpiryTime ? coupon.originalTimeframe : coupon.timeframe) // Send original timeframe when extending expiry
      };
      // console.log(payload, "payload");
      
      const response = await fetch(`${process.env.REACT_APP_API_URI}/v3/admin/coupon/step/${couponId}/deal`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: "Bearer " + authCtx.token 
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      return data.status === 'success';
    } catch (error) {
      console.error('Error updating deal details:', error);
      return false;
    }
  };

  // Update terms and conditions
  const updateTermsAndConditions = async () => {
    if (!couponId) return false;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URI}/v3/admin/coupon/step/${couponId}/terms`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: "Bearer " + authCtx.token 
        },
        body: JSON.stringify({
          termsAndConditions: termsConditions.filter(item => item.checked).map(item => item.instruction),
          instructions: instructions.filter(item => item.checked).map(item => item.instruction)
        })
      });
      
      const data = await response.json();
      return data.status === 'success';
    } catch (error) {
      console.error('Error updating terms and conditions:', error);
      return false;
    }
  };

  // Update coupon details
  const updateCouponDetails = async () => {
    if (!couponId) return false;
    
    try {
      const payload = {
        categories: couponDetails.categories,
        couponClaimUrl: couponDetails.couponClaimUrl,
        phoneNumber: couponDetails.phoneNumber,
        emailAddress: couponDetails.emailAddress,
        keywords: couponDetails.keywords,
        couponCode: couponDetails.couponCode,
      };
      
      // If checkbox is checked, send companyKeywords as true
      if (couponDetails.companyKeywords === true) {
        payload.companyKeywords = true;
      }
      
      const response = await fetch(`${process.env.REACT_APP_API_URI}/v3/admin/coupon/step/${couponId}/details`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: "Bearer " + authCtx.token 
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      return data.status === 'success';
    } catch (error) {
      console.error('Error updating coupon details:', error);
      return false;
    }
  };

  // Update locations
  const updateLocations = async () => {
    if (!couponId) return false;

    try {
      const isOnlineOnly = locationDetails.onlineOnly === "yes";
      const isGlobal = locationDetails.availabilityType === "global";
      const availability = locationDetails?.couponAvailability || (isOnlineOnly ? "online" : "instore");
      
      // Don't send locations only for online-only global coupons (not "both")
      // For "both", always send locations regardless of global/local
      // For "store", always send locations
      // For "online" + "local", send locations
      console.log("locationDetails", locationDetails);
      const locationsToSend = (availability === "both" || availability === "instore" || (availability === "online" && !isGlobal)) ? locationDetails.locations.filter(loc => 
            locationDetails.checkedId.includes(loc.id)
          )
        : [];
      console.log("locationsToSend", locationsToSend);
      const response = await fetch(`${process.env.REACT_APP_API_URI}/v3/admin/coupon/step/${couponId}/locations`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: "Bearer " + authCtx.token 
        },
        body: JSON.stringify({
          locations: locationsToSend,
          availability: availability,
          onlineOnly: isOnlineOnly,
          availabilityType: isOnlineOnly ? locationDetails.availabilityType : null,
          callToActionButton: locationDetails.callToActionButton
            .filter(btn => btn.checked)
            .map(btn => btn.name)
        })
      });
      
      const data = await response.json();
      return data.status === 'success';
    } catch (error) {
      console.error('Error updating locations:', error);
      return false;
    }
  };

  // Confirm coupon
  const confirmCoupon = async () => {
    if (!couponId) return false;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URI}/v3/admin/coupon/step/${couponId}/confirm`, {
        method: 'PATCH',
        headers: { 
          'Content-Type': 'application/json', 
          Authorization: "Bearer " + authCtx.token 
        }
      });
      
      const data = await response.json();
      return data.status === 'success';
    } catch (error) {
      console.error('Error confirming coupon:', error);
      return false;
    }
  };

  // Step 0
  // --------------------------------

  // Step 1
  const getExpirationOption = (selectedButton) => {

    switch (selectedButton) {
      case 1:
        return "Weeks";
      case 2:
        return "Months";
      case 3:
        return "Days";
      case 4:
        return "Years";
      default:
        return "Weeks";
    }
  }

  // Convert timeframe to actual days
  const convertTimeframeToDays = (value, type) => {
    switch (type) {
      case 1: // Weeks
        return value * 7;
      case 2: // Months
        return value * 30; // Approximate
      case 3: // Days
        return value;
      case 4: // Years
        return value * 365; // Approximate
      default:
        return value * 7; // Default to weeks
    }
  }

  // Get timeframe string for storage
  const getTimeframeString = (value, type) => {
    if (!value || !type) return '1 weeks'; // Default fallback
    const typeString = getExpirationOption(type).toLowerCase();
    return `${value} ${typeString}`;
  }
  
  const getSelectedDeal = () => {
    console.log(couponDealOption);
    const selected = parseInt(couponDealOption.selectedDealOptions, 10);
    if (selected === 0) {
      return `${couponDealOption.first.dealVal1}`;
    } else if (selected === 1) {
      return `${couponDealOption.second.dealVal1}`;
    } else {
      return `${couponDealOption.third.dealVal1}`;
    }
  };

  const getSelectedDealItem = () => {
    const selected = parseInt(couponDealOption.selectedDealOptions, 10);
    if (selected === 0) {
      return `${couponDealOption.first.dealVal2}`;
    }
    return null; // Only for FREE option
  };

  const getSelectedDealUnit = () => {
    const selected = parseInt(couponDealOption.selectedDealOptions, 10);
    if (selected === 0) {
      return 'ITEM';
    } else if (selected === 1) {
      return 'PER';
    } else {
      return couponDealOption.third.dealVal2.toUpperCase();
    }
  };

  const selectCouponDealOption = (_couponDealOption) => {
    setCouponDealOption(_couponDealOption);
    
    // Only calculate timeframe for draft coupons or when expiration options are available
    if (_couponDealOption.expirationOption && _couponDealOption.expirationButton) {
      // Store actual days in validFor and timeframe string
      const days = convertTimeframeToDays(_couponDealOption.expirationOption, _couponDealOption.expirationButton);
      const timeframe = getTimeframeString(_couponDealOption.expirationOption, _couponDealOption.expirationButton);
      setValidFor(days);
      updateCoupon({ timeframe: timeframe });
    }
  }

  // Step 2
  const addTermsConditions = (_termCondition) => {
    setTermsConditions([...termsConditions, _termCondition]);
  }
  const addInstructions = (_instruction) => {
    setInstructions([...instructions, _instruction]);
  }
  const toggleTermCondition = (_id) => {
    let temp = [...termsConditions];
    const target = temp.find(obj => obj.id === _id);
    if (target) {
      target.checked = !target.checked;
    }
    setTermsConditions([...temp]);
  };
  const toggleInstruction = (_id) => {
    let temp = [...instructions];
    const target = temp.find(obj => obj.id === _id);
    if (target) {
      target.checked = !target.checked;
    }
    setInstructions([...temp]);
  };
  const checkAnySelected = () => {
    const selectedInstruction = instructions.filter((item) => item.checked == true);
    const selectedTerms = termsConditions.filter((item) => item.checked == true);

    return (selectedInstruction.length && selectedTerms.length);
  }
  const checkTermsSelected = () => {
    const selectedTerms = termsConditions.filter((item) => item.checked == true);

    return (selectedTerms.length);
  }
  const checkInstructionsSelected = () => {
    const selectedInstruction = instructions.filter((item) => item.checked == true);

    return (selectedInstruction.length);
  }

  // Function to check if coupon has expired
  const isCouponExpired = () => {
    // If coupon doesn't expire, it's never expired
    if (couponDealOption.couponExpiration) {
      return false;
    }

    // If there's no validTill date, we can't determine if it's expired
    if (!couponDealOption.validTill) {
      return false;
    }

    const currentDate = new Date();
    const expiryDate = new Date(couponDealOption.validTill);
    
    return currentDate > expiryDate;
  };

  // Function to check if coupon can be confirmed
  const canConfirmCoupon = () => {
    // If coupon has expired and is in draft mode, it cannot be confirmed
    if (isCouponExpired()) {
      return false;
    }
    return true;
  };

  // Common

  const moveToNextStep = (_step) => {
    setCurrentStep(_step);
  }

  const selectCompany = (company) => {
    setSelectedCompany(company);
    updateCoupon({ selectedCompany: company });
  }

  const resetCoupon = () => {
    clearCoupon();
    setCurrentStep(0);
    setSelectedCompany(null);
    setCouponId(null);
  }

  const setError = (_keyname, show) => {
    if (!_keyname || !errors[_keyname]) {
      console.error('Invalid error key:', _keyname);
      return;
    }
    setErrors(prevErrors => ({
      ...prevErrors,
      [_keyname]: {
        ...prevErrors[_keyname],
        show: show
      }
    }));
  }

  console.log(errors);

  const handleNextStep = async (_currentStep) => {
    setSubmittingCoupon(true);
    
    try {
      switch (_currentStep) {
        case 0:
          // Skip initialization since it's already done
          setCurrentStep(_currentStep + 1);
          break;

        case 1:
          if (couponDealOption.selectedDealOptions == 0 && couponDealOption.first.dealVal1 != "" && couponDealOption.first.dealVal2 != "" && couponDealOption.description != "") {
            const success = await updateDealDetails();
            if (success) {
              setCurrentStep(_currentStep + 1);
              setError('couponDeal', false);
              setError('couponDescription', false);
            }
          } else if (couponDealOption.selectedDealOptions == 1 && couponDealOption.second.dealVal1 != "" && couponDealOption.description != "") {
            const success = await updateDealDetails();
            if (success) {
              setCurrentStep(_currentStep + 1);
              setError('couponDeal', false);
              setError('couponDescription', false);
            } 
          } else if (couponDealOption.selectedDealOptions == 2 && couponDealOption.third.dealVal1 != "" && couponDealOption.third.dealVal2 != "" && couponDealOption.description != "") {
            const success = await updateDealDetails();
            if (success) {
              setCurrentStep(_currentStep + 1);
              setError('couponDeal', false);
              setError('couponDescription', false);
            } 
          } else {
            // Check description independently
            if(!couponDealOption.description || couponDealOption.description === ""){
              setError('couponDescription', true);
            } else {
              setError('couponDescription', false);
            }
            
            // Check deal based on selected option - independent validation
            let dealError = false;
            if(couponDealOption.selectedDealOptions == 0) {
              // Option 0: Both dealVal1 and dealVal2 are required
              if(couponDealOption.first.dealVal1 === "" || couponDealOption.first.dealVal2 === "") {
                dealError = true;
              }
            } else if(couponDealOption.selectedDealOptions == 1) {
              // Option 1: Only dealVal1 is required
              if(couponDealOption.second.dealVal1 === "") {
                dealError = true;
              }
            } else if(couponDealOption.selectedDealOptions == 2) {
              // Option 2: Both dealVal1 and dealVal2 are required
              if(couponDealOption.third.dealVal1 === "" || couponDealOption.third.dealVal2 === "") {
                dealError = true;
              }
            }
            
            if(dealError) {
              setError('couponDeal', true);
            } else {
              setError('couponDeal', false);
            }
          }
          break;

        case 2:
          if (checkTermsSelected() && checkInstructionsSelected()) {
            const success = await updateTermsAndConditions();
            if (success) {
              setCurrentStep(_currentStep + 1);
              setError('terms', false);
              setError('instructions', false);
            } 
          } else {
            if(!checkTermsSelected()){
              setError('terms', true);
            }
            if(!checkInstructionsSelected()){
              setError('instructions', true);
            }
          }
          break;

        case 3:
          // Check if at least one contact method is provided (website, phone, or email)
          const hasContactMethod = couponDetails.couponClaimUrl || couponDetails.phoneNumber || couponDetails.emailAddress;
          
          if (couponDetails.categories.length != 0 && hasContactMethod && couponDetails.couponCode != "") {
            const success = await updateCouponDetails();
            if (success) {
              setCurrentStep(_currentStep + 1);
              setError('categories', false);
              setError('contactMethod', false);
              setError('code', false);
            } 
          } else {
            if(couponDetails.categories.length === 0) setError('categories', true);
            if(!hasContactMethod) setError('contactMethod', true);
            if(couponDetails.couponCode === "") setError('code', true);
          }
          break;

        case 4:
          // Check if coupon is online-only and globally available
          const isOnlineOnlyGlobal = locationDetails?.onlineOnly === "yes" && locationDetails?.availabilityType === "global" && locationDetails?.couponAvailability !== "both";
          
          let checked = locationDetails?.callToActionButton?.filter(cta => cta.checked === true);
          const hasLocationSelected = locationDetails?.checkedId?.length > 0;
          const hasLocationsAvailable = locationDetails?.locations?.length > 0;
          
          if (isOnlineOnlyGlobal) {
            // For online-only global coupons, only check call-to-action buttons
            if (checked.length > 0) {
              const success = await updateLocations();
              if (success) {
                setError('cta', false);
                setCurrentStep(_currentStep + 1);
              } 
            } else {
              setError('cta', true);
            }
          } else {
            // For regular coupons (store only, both, or online with local), check both locations and call-to-action buttons
            // Check all conditions and set all errors that fail (like step 3 does)
            let hasErrors = false;
            
            if (!hasLocationsAvailable || !hasLocationSelected) {
              setError('location', true);
              hasErrors = true;
            } else {
              setError('location', false);
            }
            
            if (checked.length === 0) {
              setError('cta', true);
              hasErrors = true;
            } else {
              setError('cta', false);
            }
            
            if (!hasErrors) {
              const success = await updateLocations();
              if (success) {
                setCurrentStep(_currentStep + 1);
              }
            }
          }
          break;

        case 5:
          // Confirmation is now handled by the modal in ConfirmCoupon component
          break;
      }
    } catch (error) {
      console.error('Error in handleNextStep:', error);
    } finally {
      setSubmittingCoupon(false);
    }
  }

  const checkErrors = () => {
    let errorsExist = false;
    Object.keys(errors).forEach(key => {
      if(errors[key].show) errorsExist =  true;
    })
    return errorsExist;
  }
  return (
    <div className='create-coupon-container-v2'>
      {currentStep === 0 && <ChooseCompany selectedCompany={selectedCompany} selectCompany={selectCompany} moveToNextStep={moveToNextStep} />}

      {currentStep >= 1 && <HeaderStepper steps={steps} currentStep={currentStep} selectedCompany={selectedCompany} resetCoupon={resetCoupon} />}

      {currentStep === 1 && <CreateCouponSection couponDealOption={couponDealOption} selectCouponDealOption={selectCouponDealOption} setValidFor={setValidFor} errors={errors} setError={setError} couponStatus={coupon.status} originalValidTill={coupon.originalValidTill} isPopin={coupon.isPopin} />}

      {currentStep === 2 && <TermsInstructions termsConditions={termsConditions} instructions={instructions} addTermsConditions={addTermsConditions} addInstructions={addInstructions} toggleTermCondition={toggleTermCondition} toggleInstruction={toggleInstruction} errors={errors} setError={setError} />}

      {currentStep === 3 && <CouponDetails couponDetails={couponDetails} setCouponDetails={setCouponDetails} orderedCategoriesData={orderedCategoriesData} setOrderedCategoriesData={setOrderedCategoriesData} errors={errors} setError={setError} />}

      {currentStep === 4 && <Location moveToNextStep={moveToNextStep} locationDetails={locationDetails} setLocationDetails={setLocationDetails} company={selectedCompany} couponDetails={couponDetails} errors={errors} setError={setError} isPopin={coupon.isPopin} />}

      {currentStep === 5 && <ConfirmCoupon selectedCompany={selectedCompany} couponDealOption={couponDealOption} couponDetails={couponDetails} orderedCategoriesData={orderedCategoriesData} termsConditions={termsConditions} instructions={instructions} locationDetails={locationDetails} isExpired={isCouponExpired()} couponId={couponId} onBack={() => setCurrentStep(4)} />}


      {
        currentStep >= 1 && currentStep < 5 &&
        <div className='footer-buttons '>
          {/* Disable back button in edit mode for step 1 (Deal part) */}
          <CustomButton 
            className={'deliva-btn-bordered'} 
            onClick={() => setCurrentStep(currentStep - 1)} 
            disabled={submittingCoupon || (currentStep === 1 && (couponId || coupon.couponId))}
          >
            Back
          </CustomButton>
          <CustomButton 
            onClick={() => submittingCoupon ? null : handleNextStep(currentStep)} 
            disabled={submittingCoupon}
          >
            {submittingCoupon ? <CircularProgress size={20} /> : "Next"}
          </CustomButton>
        </div>
      }
    </div>
  )
}
